/* eslint-disable react/no-array-index-key */
import React, { Suspense, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSnackbar } from 'notistack';
import { updateUserData } from 'src/actions/accountActions';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import {
    Box,
    Button,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Typography,
    CircularProgress,
    IconButton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Close as CloseIcon,
    CloudDone as UploadCompleteIcon,
    CloudOff as UploadFailedIcon,
    PictureAsPdf as PDFIcon,
} from '@material-ui/icons';
import bytesToSize from 'src/utils/bytesToSize';
import claimsDocumentService from '../../../../services/claimsDocumentService';
import { useSelector } from 'react-redux';
import { getTimestampedFileName } from 'src/utils/file';
import { getFileThumbnail } from 'src/utils/file';
import FilePreview from 'src/components/FilePreview';
import { PDF_HEX_COLOR } from 'src/constants/fileIconColor';

const DOC_NAME_MAX_LENGTH = 50;
const useStyles = makeStyles(theme => ({
    root: {},
    dropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        padding: theme.spacing(0.5, 1),
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: theme.spacing(0.75),
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            border: `2px dashed ${theme.palette.primary.main}`,
            cursor: 'pointer'
        }
    },
    dragActive: {
        border: `2px dashed ${theme.palette.secondary.main}`,
        opacity: 0.8
    },
    image: {
        width: 35,
        marginRight: theme.spacing(1)
    },
    info: {
        marginTop: theme.spacing(1)
    },
    list: {
        maxHeight: 320
    },
    actions: {
        marginTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        '& > * + *': {
            marginLeft: theme.spacing(2)
        }
    },
    fileUploadItem: {
        padding: theme.spacing(1, 1),
        transition: '0.3s background-color',

        '&:hover': {
            backgroundColor: '#04A4E21c',
            borderRadius: theme.spacing(1)
        }
    },
    uploadButton: {
        '&:disabled': {
            backgroundColor: 'rgba(0, 155, 218, 0.20)',
        }
    },
    preview: {
        borderRadius: 4,
        maxWidth: 60,
        height: 40
    },
    pdfIcon: {

    }
}));

function FilesDropzone({
    className,
    uploadComplete,
    claim,
    claimContactInformation,
    maxFilesCountToUpload,
    maxFilesCountForCategory,
    fromGlassSearchView,
    userEmail = '',
    userName = '',
    ...rest
}) {
    const uploadMessageRef = useRef();
    const classes = useStyles();
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileStatuses = useRef([]);
    const [numSuccessfulUploads, setNumSuccessfulUploads] = useState(0);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const theme = useTheme();
    const [allowedFilesToAdd, setAllowedFilesToAdd] = useState(maxFilesCountToUpload);
    const [fileDropErrors, setFileDropErrors] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const dealerUser = useSelector(state => state.dealerAccount.dealerUser);
    const { user } = useSelector(state => state.account);
    const [showUserInfoDialog, setShowUserInfoDialog] = useState(false);
    const [userInfoError, setUserInfoError] = useState('');
    let [docUserEmail, setDocUserEmail] = useState(userEmail || '');
    let [docUserName, setDocUserName] = useState(userName || '');
    const dispatch = useDispatch();

    let filesLeftLabel = files.length - numSuccessfulUploads !== 1 ? 'files' : 'file';
    let totalFilesLabel = files.length - numSuccessfulUploads !== 1 ? 'files' : 'file';
    let progressSnackbar;

    const fileDropCodes = {
        fileTooLarge: 'file-too-large',
        fileInvalidType: 'file-invalid-type',
        tooManyFiles: 'too-many-files',
    };
    const maxFileSize = claim.claimSource == 'ECP' ? '10Mb' : '100Mb';
    const fileDropErrorMessageMap = {
        [fileDropCodes.fileTooLarge]: `File is too large. Max allowed size is ${maxFileSize}.`,
        [fileDropCodes.fileInvalidType]: 'You can only upload .jpg, .jpeg, and .pdf files',
        [fileDropCodes.tooManyFiles]: `You can upload maximum ${maxFilesCountForCategory} files for this category.`
    }
    const handleDrop = useCallback((acceptedFiles, fileRejections) => {
        setFileDropErrors([]);
        if (acceptedFiles.length) {
            fileStatuses.current = [];
        }

        setFiles(
            prevFiles => [
                ...prevFiles,
                ...acceptedFiles
            ].map((file) => Object.assign(file, { preview: URL.createObjectURL(file) }))
        );

        if (fileRejections.length) {
            handleRejectedFiles(fileRejections);
        }
    }, []);

    const handleRejectedFiles = (fileRejections) => {
        let errorsMessages = [];
        const errors = fileRejections.map(file => file.errors.map(err => err.code)).flat();

        if (errors.indexOf(fileDropCodes.fileTooLarge) >= 0) {
            errorsMessages.push(fileDropErrorMessageMap[fileDropCodes.fileTooLarge]);
        }
        if (errors.indexOf(fileDropCodes.fileInvalidType) >= 0) {
            errorsMessages.push(fileDropErrorMessageMap[fileDropCodes.fileInvalidType]);
        }
        if (errors.indexOf(fileDropCodes.tooManyFiles) >= 0) {
            errorsMessages.push(fileDropErrorMessageMap[fileDropCodes.tooManyFiles]);
        }
        setFileDropErrors(errorsMessages);
    }

    const handleRemoveAll = () => {
        setFiles([]);
        setFileDropErrors([]);
    };

    const handleRemoveFile = (file) => {
        setFiles(prevFiles => [...prevFiles].filter(f => f !== file));
        setFileDropErrors([]);
    }
    const maxFilesValidator = () => {
        if (allowedFilesToAdd <= 0) {
            return {
                code: 'too-many-files',
                message: 'Too many files.',
            };
        }
        return null;
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        open
    } = useDropzone({
        onDrop: handleDrop,
        accept: 'image/jpeg, image/png, image/jpg, application/pdf, video/quicktime, video/mp4',
        validator: maxFilesValidator,
        maxSize: 104857600, //allow max 100Mb file size
        noClick: true,
        ...(claim.claimSource == 'ECP'
            ? {
                maxFiles: allowedFilesToAdd,
                maxSize: 10485760,
                accept: 'image/jpeg, image/jpg, application/pdf'
            }
            : {})
    });

    const handleBrowseClick = (event) => {
        event.preventDefault();
        open();
    };

    const dropzoneRootProps = getRootProps();

    const showProgressSnackbar = () => {
        progressSnackbar = enqueueSnackbar(
            <span ref={uploadMessageRef}>
                Uploading {files.length - numSuccessfulUploads} {filesLeftLabel}
            </span>,
            {
                variant: 'default',
                persist: true,
                action: <CircularProgress size={20} color="inherit" />
            }
        );
    }

    const hideProgressSnackbar = () => {
        closeSnackbar(progressSnackbar);
    }

    /** @param {string} newFileName */
    const createDocumentPayload = (newFileName) => {
        const fallbackEmail = fromGlassSearchView
            ? userEmail
            : (user ? user.email : claimContactInformation.emailAddress);

        return {
            documentName: newFileName,
            createdBy: docUserName,
            documentCategory: 'Photos',
            documentSubCategory: 'Photos',
            claimSource: 'VENDOR',
            claimNumber: claim.claimNumber,
            dealerCode: claim.dealerCode,
            createdByEmail: docUserEmail || fallbackEmail,
        };
    }

    /** @param {number} numFailedUploads */
    const finishUploadingFiles = (numFailedUploads) => {
        if (numFailedUploads === 0) {
            enqueueSnackbar(
                `Successfully uploaded ${files.length} ${totalFilesLabel}`,
                { variant: 'success' }
            );
        }
        hideProgressSnackbar();
        setIsUploading(false);
        setNumSuccessfulUploads(0);
        setFiles([]);
    }


    const handleUserInfoSubmit = async () => {
        if ((!docUserName.trim() || !docUserEmail.trim()) && (!user && !dealerUser)) {
            setUserInfoError(t('dropzone.userInfoRequired'));
            return;
        }

        setUserInfoError('');
        setShowUserInfoDialog(false);
        if (!dealerUser && !user) {
            dispatch(updateUserData({
                email: docUserEmail,
                fullName: docUserName
            }));
        }
        else if (dealerUser && !user) {
            docUserEmail = dealerUser.email;
            docUserName = dealerUser.fullName || '';
        }
        else {
            docUserEmail = user.email;
            docUserName = user.fullName || '';
        }


        // Now proceed with the actual upload logic
        let numFailedUploads = 0;
        if (fileStatuses.current.includes('success')) {
            return;
        }
        try {
            setIsUploading(true);
            showProgressSnackbar();

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const newFileName = getTimestampedFileName(file.name, DOC_NAME_MAX_LENGTH);
                // Add user info to documentAttribute
                const documentAttribute = {
                    ...createDocumentPayload(newFileName),
                    docUserName,
                    docUserEmail
                };
                const result = await claimsDocumentService.uploadFile(file, claim.claimNumber, documentAttribute);

                if (result.data.success) {
                    setNumSuccessfulUploads(prevState => prevState + 1);
                    fileStatuses.current[i] = 'success';
                } else {
                    numFailedUploads++;
                    enqueueSnackbar(`Upload failed for ${file.name}`, { variant: 'error' });
                    fileStatuses.current[i] = 'failure';
                }
            }

            if (uploadComplete) await uploadComplete(claim.claimSource);
        } catch (error) {
            console.log(error);
            numFailedUploads++;
            enqueueSnackbar(
                `Failed to upload your ${totalFilesLabel}`,
                { variant: 'error' }
            );
        } finally {
            finishUploadingFiles(numFailedUploads);
        }
    };

    const handleUserInfoCancel = () => {
        setShowUserInfoDialog(false);
        setDocUserName('');
        setDocUserEmail('');
        setUserInfoError('');
    };

    const uploadFiles = async () => {
        if (!user && !dealerUser) {
            setShowUserInfoDialog(true);
        }
        else {
            handleUserInfoSubmit();
        }
    };

    const handleSelectFile = (file) => {
        setSelectedFile(file);
    }

    const closeFilePreview = () => {
        setSelectedFile(null);
    }

    useEffect(() => {
        if (uploadMessageRef.current) {
            const element = uploadMessageRef.current;
            const filesLeftValue = files.length - numSuccessfulUploads;
            const filesLeftLabel = filesLeftValue !== 1 ? 'files' : 'file';
            element.textContent = `Uploading ${filesLeftValue} ${filesLeftLabel}`;

            if (filesLeftValue === 0) {
                element.textContent = 'Finishing upload';
            }
        }
    }, [numSuccessfulUploads]);

    useEffect(() => {
        const remainingFilesToUpload = maxFilesCountToUpload - files?.length;
        setAllowedFilesToAdd(remainingFilesToUpload == 0 ? -1 : remainingFilesToUpload);

        return () => {
            for (const file of files) {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            }
        }
    }, [files]);

    /** @param {number} fileIndex */
    const StatusIcon = (fileIndex) => {
        const status = fileStatuses.current[fileIndex];
        const file = files[fileIndex];

        switch (status) {
            case 'success':
                return (
                    <ListItemIcon>
                        <UploadCompleteIcon
                            style={{ color: theme.palette.success.main }}
                        />
                    </ListItemIcon>
                );
            case 'failure':
                return (
                    <ListItemIcon>
                        <UploadFailedIcon
                            style={{ color: theme.palette.error.main }}
                        />
                    </ListItemIcon>
                );
            default: {
                if (file.type === 'application/pdf') {
                    return (
                        <Suspense fallback={<PDFIcon htmlColor={PDF_HEX_COLOR} />}>
                            {React.lazy(async () => await getFileThumbnail(file))}
                        </Suspense>
                    );
                }
                return (
                    <ListItemIcon>
                        <img
                            className={classes.preview}
                            src={file.preview}
                            width="95%"
                            height="95%"
                        />
                    </ListItemIcon>
                );
            }
        }
    }

    const Files = useMemo(() => {
        return (
            <PerfectScrollbar options={{ suppressScrollX: true }}>
                <List className={classes.list}>
                    {files.map((file, i) => (
                        <ListItem
                            className={classes.fileUploadItem}
                            divider={i < files.length - 1} key={i}
                            onClick={() => handleSelectFile(file)}
                        >
                            {StatusIcon(i)}
                            <ListItemText
                                primary={file.name}
                                primaryTypographyProps={{ variant: 'h5' }}
                                secondary={bytesToSize(file.size)}
                                style={{ marginLeft: 8 }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleRemoveFile(file)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </PerfectScrollbar>
        );
    }, [files.length]);

    return (
        <div className={clsx(classes.root, className)} {...rest}>
            <Dialog open={showUserInfoDialog} onClose={handleUserInfoCancel} maxWidth="xs" fullWidth>
                <DialogTitle>{t('dropzone.dialogTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('dropzone.fullName')}
                        type="text"
                        fullWidth
                        value={docUserName || ''}
                        onChange={e => setDocUserName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label={t('dropzone.emailAddress')}
                        type="email"
                        fullWidth
                        value={docUserEmail || ''}
                        onChange={e => setDocUserEmail(e.target.value)}
                    />
                    {userInfoError && (
                        <Box color="error.main" mt={1}>
                            {userInfoError}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserInfoCancel} color="default">
                        {t('dropzone.cancel')}
                    </Button>
                    <Button onClick={handleUserInfoSubmit} color="primary" variant="contained">
                        {t('dropzone.submit')}
                    </Button>
                </DialogActions>
            </Dialog>
            <div
                className={clsx({
                    [classes.dropZone]: true,
                    [classes.dragActive]: isDragActive
                })}
                {...dropzoneRootProps}
            >
                <input {...getInputProps()} />
                <Box mt={1} display="inline-flex" zIndex={1}>
                    <img
                        alt={t('dropzone.selectFileAlt')}
                        className={classes.image}
                        src="/static/images/undraw_add_file2_gvbb.svg"
                    />
                    {allowedFilesToAdd > 0 &&
                        <Typography color="textPrimary" variant="body1" style={{ alignSelf: 'center' }}>
                            <Trans
                                i18nKey="dropzone.dropHereOrBrowse"
                                components={{ browse: <Link id="browse" underline="always" onClick={handleBrowseClick} /> }}
                            />
                        </Typography>
                    }
                </Box>
                <FilePreview file={selectedFile} onClose={closeFilePreview} />
                {files.length > 0 && (
                    <Box width="100%">
                        {Files}
                        <Divider />
                        <div className={classes.actions}>
                            <Button onClick={handleRemoveAll} size="small">
                                {t('dropzone.removeAll')}
                            </Button>
                            <Button
                                color="secondary"
                                size="small"
                                variant="contained"
                                className={classes.uploadButton}
                                onClick={uploadFiles}
                                disabled={isUploading || fileStatuses.current.includes('success')}
                            >
                                {isUploading
                                    ? <CircularProgress color="#fff" size={15} />
                                    : t('dropzone.uploadFiles')
                                }
                            </Button>
                        </div>
                    </Box>
                )}
            </div>
            <div>
                {fileDropErrors.length > 0 && fileDropErrors.map(errorMessage =>
                    <Typography color={'error'} variant={'subtitle2'} key={errorMessage}>
                        {errorMessage}
                    </Typography>
                )}
            </div>
        </div>
    );
}

FilesDropzone.propTypes = {
    className: PropTypes.string,
    uploadComplete: PropTypes.func,
    claim: PropTypes.shape({
        claimNumber: PropTypes.string,
        dealerCode: PropTypes.string,
        claimSource: PropTypes.string
    }),
    claimContactInformation: PropTypes.shape({
        emailAddress: PropTypes.string
    }),
    maxFilesCountToUpload: PropTypes.number,
    maxFilesCountForCategory: PropTypes.number,
    fromGlassSearchView: PropTypes.bool,
    userEmail: PropTypes.string,
    userName: PropTypes.string
};

export default FilesDropzone;
